export const PULL_THRESHOLD = 70;
export const PULL_MAX = 128;
export const PULL_REFRESH_HOLD = 64;
export const DIRECTION_SLOP = 8;
export const MIN_REFRESH_MS = 400;

export type PullPhase = 'idle' | 'tracking' | 'pulling' | 'refreshing';

export interface PullState {
  phase: PullPhase;
  originX: number;
  originY: number;
  pull: number;
}

export type PullEvent =
  | { type: 'start'; x: number; y: number }
  | { type: 'move'; x: number; y: number }
  | { type: 'end' }
  | { type: 'cancel' }
  | { type: 'refreshed' };

export const IDLE_PULL: PullState = { phase: 'idle', originX: 0, originY: 0, pull: 0 };

// Asymptotic resistance: the indicator tracks the finger closely at first and never passes PULL_MAX.
export function rubberBand(dy: number): number {
  if (dy <= 0) return 0;
  return PULL_MAX * (1 - Math.exp(-dy / PULL_MAX));
}

export function isArmed(state: PullState): boolean {
  return state.phase === 'pulling' && state.pull >= PULL_THRESHOLD;
}

export function reducePull(state: PullState, event: PullEvent): PullState {
  switch (event.type) {
    case 'start':
      if (state.phase === 'refreshing' || state.phase === 'pulling') return state;
      return { phase: 'tracking', originX: event.x, originY: event.y, pull: 0 };
    case 'move': {
      if (state.phase !== 'tracking' && state.phase !== 'pulling') return state;
      const dx = event.x - state.originX;
      const dy = event.y - state.originY;
      if (state.phase === 'pulling') return { ...state, pull: rubberBand(dy) };
      if (Math.max(Math.abs(dx), Math.abs(dy)) < DIRECTION_SLOP) return state;
      if (dy > 0 && dy > Math.abs(dx)) return { ...state, phase: 'pulling', pull: rubberBand(dy) };
      return IDLE_PULL;
    }
    case 'end':
      if (isArmed(state)) return { ...state, phase: 'refreshing', pull: PULL_REFRESH_HOLD };
      if (state.phase === 'refreshing') return state;
      return IDLE_PULL;
    case 'cancel':
      return state.phase === 'refreshing' ? state : IDLE_PULL;
    case 'refreshed':
      return state.phase === 'refreshing' ? IDLE_PULL : state;
  }
}

export interface PullGate {
  standalone: boolean;
  coarsePointer: boolean;
  customerSurface: boolean;
  overlayOpen: boolean;
}

export function pullToRefreshAvailable({ standalone, coarsePointer, customerSurface }: PullGate): boolean {
  return standalone && coarsePointer && !customerSurface;
}

export function pullToRefreshEnabled(gate: PullGate): boolean {
  return pullToRefreshAvailable(gate) && !gate.overlayOpen;
}

// A pull only starts when both the page and every scrollable ancestor of the touch are at the top;
// otherwise the gesture belongs to whatever is scrolled.
export function canStartPull(target: EventTarget | null, pageScrollTop: number): boolean {
  if (pageScrollTop > 0) return false;
  for (let node = target instanceof Element ? target : null; node; node = node.parentElement) {
    if (node.scrollTop > 0) return false;
  }
  return true;
}
